import { call, fork, put, select } from 'redux-saga/effects';
import { getFileInfoById } from "../../file";
import { INTELLIGENT_PHOTO_CAPTURE_DOCUMENT_BATCH_PROCESSING_REQUEST, photoCaptureDataFailureAction, photoCaptureIntelligentAddEstimatedSummaryFileInfoAction, photoCaptureIntelligentDeleteAttachmentIterationAction, photoCaptureIntelligentUpdateIterationUploadFailedCountAction, photoCaptureIntelligentUpdateUploadedFilesCountAction } from '../actions';
import { IntelligentAssignmentSummary, SERVICE_FAIL_STATUS } from '../constants';
import { IntelligentPhotoCaptureImageBatchProcessorType } from '../enums';
import { getAttachmentIterationInfo, getIntelligentMaxConcurrentBatchAllowed, getIntelligentMaxPhotoLimitForBatch } from '../selectors';
import { attachmentIteration, createBatchProcessorChain, isFileUploadStatusFailed } from '../utilities';
import { intelligentPhotoCaptureBatchUploadSaga } from './intelligentPhotoCaptureBatchUploadSaga';
import { intelligentPhotoCaptureDocumentBatchStatusSaga } from './intelligentPhotoCaptureDocumentBatchStatusSaga';
import { intelligentPhotoCaptureFailureLoggingSaga } from './intelligentPhotoCaptureFailureLoggingSaga';
/**
 * Helper generator to get the upload file batch config.
 * Returns config with UPDATE_PATCH and UPLOAD_FILES_BATCH_STATUS processors only.
 */
export function* getUploadFileBatchConfig() {
    const batchSize = yield select(getIntelligentMaxPhotoLimitForBatch);
    const maxConcurrentBatches = yield select(getIntelligentMaxConcurrentBatchAllowed);
    return {
        batchSize,
        maxConcurrentBatches,
        processors: [
            {
                name: IntelligentPhotoCaptureImageBatchProcessorType.UPDATE_PATCH,
                fn: function* (batch, batchId) {
                    return yield call(intelligentPhotoCaptureBatchUploadSaga, batch, batchId);
                }
            },
            {
                name: IntelligentPhotoCaptureImageBatchProcessorType.UPLOAD_FILES_BATCH_STATUS,
                fn: function* (batch, batchId) {
                    yield call(intelligentPhotoCaptureDocumentBatchStatusSaga, batch, batchId);
                }
            }
        ]
    };
}
/**
 * Batch processor chain instance for handling upload file processing
 */
export const uploadFileBatchChain = createBatchProcessorChain();
/**
 * Saga that processes uploaded files in batches for the UPLOAD_FILES capture mode.
 * Uses UPDATE_PATCH and UPLOAD_FILES_BATCH_STATUS processors.
 * Only processes primary images (not thumbnails).
 *
 * @param action - The file upload complete action
 * @yields Effects for Redux-Saga
 */
export function* intelligentPhotoCaptureDocumentBatchProcessingSaga(action) {
    try {
        const { id } = action.payload;
        const fileInfo = yield select(getFileInfoById, id);
        if (!fileInfo) {
            throw new Error(IntelligentAssignmentSummary.FILE_NOT_FOUND);
        }
        const currentInProgressIteration = attachmentIteration.getInProgressIteration(yield select(getAttachmentIterationInfo));
        const { iterationId, uploadedFilesCount = 0, uploadFailedCount = 0, iterationFiles } = currentInProgressIteration || {};
        if (isFileUploadStatusFailed(fileInfo)) {
            if (iterationFiles && iterationFiles.length && iterationId !== undefined) {
                yield put(photoCaptureIntelligentUpdateIterationUploadFailedCountAction(iterationId, 1));
                if (iterationFiles.length === uploadFailedCount + IntelligentAssignmentSummary.UPLOADED_FILE_COUNT_INCREMENT) {
                    yield put(photoCaptureIntelligentDeleteAttachmentIterationAction(iterationId));
                    yield fork(intelligentPhotoCaptureFailureLoggingSaga, {
                        actionType: INTELLIGENT_PHOTO_CAPTURE_DOCUMENT_BATCH_PROCESSING_REQUEST,
                        errorDetail: SERVICE_FAIL_STATUS,
                        requestedPayload: fileInfo,
                        response: IntelligentAssignmentSummary.EDS_FILE_CREATION_ERROR
                    });
                }
            }
            return;
        }
        yield put(photoCaptureIntelligentAddEstimatedSummaryFileInfoAction(fileInfo));
        const file = yield select(getFileInfoById, fileInfo.id);
        if (!file) {
            throw new Error(IntelligentAssignmentSummary.FILE_NOT_FOUND);
        }
        const uploadFileBatchConfig = yield call(getUploadFileBatchConfig);
        yield call([uploadFileBatchChain, uploadFileBatchChain.addItemToProcessor], file, uploadFileBatchConfig);
        if (iterationFiles && iterationFiles.length && iterationId !== undefined) {
            yield put(photoCaptureIntelligentUpdateUploadedFilesCountAction(iterationId));
            const fileCount = uploadedFilesCount + IntelligentAssignmentSummary.UPLOADED_FILE_COUNT_INCREMENT;
            if (iterationFiles.length - uploadFailedCount === fileCount &&
                fileCount % uploadFileBatchConfig.batchSize !== 0) {
                yield call([uploadFileBatchChain, uploadFileBatchChain.flushRemainingBatch], uploadFileBatchConfig);
            }
        }
    }
    catch (error) {
        yield put(photoCaptureDataFailureAction(error.toString()));
    }
}
//# sourceMappingURL=intelligentPhotoCaptureDocumentBatchProcessingSaga.js.map